import { Divider, Grid, Popover, Switch } from '@mui/material';
import { AvatarIcon, HSpacer, RoutalPalette, RoutalTooltip, VSpacer } from '@nx-smartmonkey/ui';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import AlertFilterIcon from '../../../../assets/img/map_filters/alert.svg';
import ArrowFilterIcon from '../../../../assets/img/map_filters/arrow.svg';
import FiltersMenuIcon from '../../../../assets/img/map_filters/filters_menu.svg';
import GeoFencesFilterIcon from '../../../../assets/img/map_filters/geo_fences.svg';
import LineFilterIcon from '../../../../assets/img/map_filters/line.svg';
import MarkerFilterIcon from '../../../../assets/img/map_filters/marker.svg';
import VisibilityOffIcon from '../../../../assets/svg/VisibilityOffIcon';
import VisibilityOnIcon from '../../../../assets/svg/VisibilityOnIcon';
import RouteAvatar from '../../../../components/RouteAvatar';
import * as routing from '../../../../constants/Routing';
import { PLAN_TYPE } from '../../../../constants/Subscriptions';
import { Driver } from '../../../../domain/driver/Driver';
import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { useRetrieveOrganization } from '../../../../hooks/organization/useRetrieveOrganization';
import { usePlanMapFilters } from '../../../../hooks/plan/usePlanMapFilters';
import { useRetrieveActivePlan } from '../../../../hooks/plan/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../../../../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActivePlanGeoFences } from '../../../../hooks/plan/useRetrieveActivePlanGeoFences';
import { useUpdateActivePlanGeoFences } from '../../../../hooks/plan/useUpdateActivePlanGeoFences';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActiveProjectGeoFences } from '../../../../hooks/project/useRetrieveActiveProjectGeoFences';
import { Accordion } from '../../../../react-components/Accordion';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';

const FILTERS_MENU_HEIGHT = 620;
const FILTERS_MENU_WIDTH = 340;
const ZONES_MENU_HEIGHT = 186;
const ZONES_MENU_WIDTH = 340;

const EyeSelectorContainer = styled(Grid)`
  width: 48px !important;
  height: 32px;
  cursor: pointer;
  justify-content: center;
  align-items: center;
`;

const MapFilterElement = styled(Grid)`
  height: 42px;
`;

interface MapFiltersProps {
  setCreatingGeoFence: (isCreating: boolean) => void;
}

export const MapFilters = ({ setCreatingGeoFence }: MapFiltersProps) => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { data: organization } = useRetrieveOrganization();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: activePlan } = useRetrieveActivePlan();
  const { data: activePlanGeoFences } = useRetrieveActivePlanGeoFences();
  const { data: activeProjectGeoFences } = useRetrieveActiveProjectGeoFences();

  const { run: updatePlanGeoFences } = useUpdateActivePlanGeoFences();

  const { driversFilters, setDriversFilters, symbolsFilters, setSymbolsFilters } = usePlanMapFilters();

  const { data: drivers } = useRetrieveActivePlanDrivers();

  const [popoverOpen, setPopoverOpen] = useState<
    | {
        type: `filters` | `zones`;
        top: number;
        left: number;
      }
    | undefined
  >(undefined);

  const planGeoFences = useMemo(() => (activePlanGeoFences ?? []).filter((geo) => geo.enabled), [activePlanGeoFences]);

  const projectGeoFences = useMemo(
    () => (activeProjectGeoFences ?? []).filter((geoFence) => geoFence.enabled),
    [activeProjectGeoFences]
  );

  const geoFencesEnabled = useMemo(
    () => organization?.subscription.plan !== PLAN_TYPE.MONKEY && activeProject?.geo_fences_enabled,
    [activeProject?.geo_fences_enabled, organization?.subscription.plan]
  );

  const allSymbolsFiltersAreEnabled = () =>
    Object.keys(symbolsFilters).every(
      (symbolsFiltersKey: string) => (symbolsFilters as any)[symbolsFiltersKey] === true
    );

  const allDriversAreEnabled = () =>
    [`unassigned`, ...(drivers ?? []).map((driver) => driver.id)].every(
      (driverId) => driversFilters[driverId] === true
    );

  const allZonesAreEnabled = () => projectGeoFences.length === planGeoFences.length;

  const onUpdateActiveZones = ({ selectedZones }: { selectedZones: GeoFence[] }) => {
    updatePlanGeoFences({ geo_fences: selectedZones });
  };

  if (!activePlan || !drivers) {
    return null;
  }

  return (
    <>
      <Button
        onClick={(event: any) => {
          event.stopPropagation();
          const boundingClient = event.currentTarget.getBoundingClientRect();
          setPopoverOpen({
            type: `filters`,
            top: boundingClient.top - FILTERS_MENU_HEIGHT - 6,
            left: boundingClient.left,
          });
        }}
        variant="flat"
        styleType="secondary"
        style={{
          backgroundColor: RoutalPalette.white,
          margin: 0,
          width: `70px`,
          justifyContent: `flex-start`,
          borderColor: RoutalPalette.secondary00,
        }}
      >
        <img src={FiltersMenuIcon} alt="" draggable="false" style={{ width: `14px`, marginRight: `4px` }} />
        <div style={{ position: `relative` }}>
          <Text style={{ cursor: `pointer` }} intlId="map_options.filters" />
          {!allSymbolsFiltersAreEnabled() || !allDriversAreEnabled() ? (
            <span
              style={{
                width: `6px`,
                height: `6px`,
                backgroundColor: RoutalPalette.primary40,
                borderRadius: `50%`,
                position: `absolute`,
                top: `0`,
                right: `-5px`,
              }}
            ></span>
          ) : null}
        </div>
      </Button>

      {popoverOpen?.type === `filters` ? (
        <Popover
          PaperProps={{
            variant: `outlined`,
            style: {
              overflow: `hidden`,
            },
          }}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: popoverOpen.top,
            left: popoverOpen.left,
          }}
          keepMounted
          open={popoverOpen?.type === `filters`}
          onClose={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
            setPopoverOpen(undefined);
          }}
        >
          <div
            style={{
              width: `${ZONES_MENU_WIDTH}px`,
              height: `${FILTERS_MENU_HEIGHT}px`,
            }}
          >
            <div
              style={{
                height: `310px`,
              }}
            >
              <Accordion
                id="map_filters_symbols"
                expanded
                headerStyles={{
                  padding: `8px`,
                }}
                expandIconStyles={{
                  marginTop: 8,
                }}
                headerContent={
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item xs={10}>
                      <Text
                        variant="body-large"
                        isBold
                        intlId="map_options.filters.symbols"
                        style={{ cursor: `pointer` }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <RoutalTooltip
                        title={intl.formatMessage({
                          id: `map_options.filters.${allSymbolsFiltersAreEnabled() ? `hide_all` : `show_all`}`,
                        })}
                      >
                        <Button
                          styleType="secondary"
                          onClick={() => {
                            setSymbolsFilters({
                              markers: !allSymbolsFiltersAreEnabled(),
                              lines: !allSymbolsFiltersAreEnabled(),
                              alerts: !allSymbolsFiltersAreEnabled(),
                              zones: !allSymbolsFiltersAreEnabled(),
                              arrows: !allSymbolsFiltersAreEnabled(),
                            });
                          }}
                          style={{
                            padding: 0,
                            margin: 0,
                            height: `32px`,
                            width: `42px`,
                            backgroundColor: allSymbolsFiltersAreEnabled()
                              ? RoutalPalette.text
                              : RoutalPalette.primary40,
                          }}
                        >
                          {allSymbolsFiltersAreEnabled() ? <VisibilityOffIcon /> : <VisibilityOnIcon />}
                        </Button>
                      </RoutalTooltip>
                    </Grid>
                  </MapFilterElement>
                }
                contentStyle={{ flex: 1 }}
              >
                <Grid container style={{ paddingLeft: `12px`, paddingRight: `8px` }}>
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                      <img
                        src={MarkerFilterIcon}
                        alt=""
                        draggable="false"
                        style={{ width: `16px`, marginLeft: `6px` }}
                      />

                      <Text style={{ margin: `8px` }} intlId="map_options.filters.marker_locations" />
                    </Grid>

                    <Grid item xs={2}>
                      {symbolsFilters.markers ? (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              markers: false,
                            });
                          }}
                        >
                          <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      ) : (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              markers: true,
                            });
                          }}
                        >
                          <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      )}
                    </Grid>
                  </MapFilterElement>
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                      <img src={LineFilterIcon} alt="" draggable="false" style={{ width: `16px`, marginLeft: `6px` }} />

                      <Text style={{ margin: `8px` }} intlId="map_options.filters.route_lines" />
                    </Grid>

                    <Grid item xs={2}>
                      {symbolsFilters.lines ? (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              lines: false,
                            });
                          }}
                        >
                          <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      ) : (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              lines: true,
                            });
                          }}
                        >
                          <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      )}
                    </Grid>
                  </MapFilterElement>
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                      <img
                        src={ArrowFilterIcon}
                        alt=""
                        draggable="false"
                        style={{ width: `16px`, marginLeft: `6px` }}
                      />

                      <Text style={{ margin: `8px` }} intlId="map_options.filters.direction_arrows" />
                    </Grid>

                    <Grid item xs={2}>
                      {symbolsFilters.arrows ? (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              arrows: false,
                            });
                          }}
                        >
                          <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      ) : (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              arrows: true,
                            });
                          }}
                        >
                          <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      )}
                    </Grid>
                  </MapFilterElement>
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                      <img
                        src={AlertFilterIcon}
                        alt=""
                        draggable="false"
                        style={{ width: `16px`, marginLeft: `6px` }}
                      />

                      <Text style={{ margin: `8px` }} intlId="map_options.filters.alerts" />
                    </Grid>

                    <Grid item xs={2}>
                      {symbolsFilters.alerts ? (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              alerts: false,
                            });
                          }}
                        >
                          <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      ) : (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              alerts: true,
                            });
                          }}
                        >
                          <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      )}
                    </Grid>
                  </MapFilterElement>
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                      <img
                        src={GeoFencesFilterIcon}
                        alt=""
                        draggable="false"
                        style={{ width: `16px`, marginLeft: `6px` }}
                      />

                      <Text style={{ margin: `8px` }} intlId="map_options.filters.zones" />
                    </Grid>

                    <Grid item xs={2}>
                      {symbolsFilters.zones ? (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              zones: false,
                            });
                          }}
                        >
                          <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      ) : (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            setSymbolsFilters({
                              ...symbolsFilters,
                              zones: true,
                            });
                          }}
                        >
                          <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      )}
                    </Grid>
                  </MapFilterElement>
                  {` `}
                </Grid>
              </Accordion>
            </div>

            <Divider />

            <div
              style={{
                height: `310px`,
              }}
            >
              <Accordion
                id="map_filters_drivers"
                expanded
                headerStyles={{
                  padding: `8px`,
                }}
                expandIconStyles={{
                  marginTop: 8,
                }}
                style={{
                  height: `100%`,
                  flexWrap: `nowrap`,
                }}
                headerContent={
                  <MapFilterElement
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item xs={10}>
                      <Text
                        variant="body-large"
                        isBold
                        intlId="map_options.filters.vehicles"
                        style={{ cursor: `pointer` }}
                      />
                    </Grid>

                    <Grid item xs={2}>
                      <RoutalTooltip
                        title={intl.formatMessage({
                          id: `map_options.filters.${allDriversAreEnabled() ? `hide_all` : `show_all`}`,
                        })}
                      >
                        <Button
                          styleType="secondary"
                          onClick={() => {
                            const newDriversFilters = {
                              [`unassigned`]: !allDriversAreEnabled(),
                              ...(drivers ?? []).reduce((amount, d) => {
                                return {
                                  ...amount,
                                  [d.id]: !allDriversAreEnabled(),
                                };
                              }, {}),
                            };
                            setDriversFilters(newDriversFilters);
                          }}
                          style={{
                            padding: 0,
                            margin: 0,
                            height: `32px`,
                            width: `42px`,
                            backgroundColor: allDriversAreEnabled() ? RoutalPalette.text : RoutalPalette.primary40,
                          }}
                        >
                          {allDriversAreEnabled() ? <VisibilityOffIcon /> : <VisibilityOnIcon />}
                        </Button>
                      </RoutalTooltip>
                    </Grid>
                  </MapFilterElement>
                }
                contentStyle={{ flex: 1, overflow: `auto` }}
              >
                <Grid container style={{ paddingLeft: `12px`, paddingRight: `8px` }}>
                  <Grid
                    key="unassigned-stops"
                    container
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    wrap="nowrap"
                  >
                    <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                      <AvatarIcon
                        color={RoutalPalette.statusColors.pending}
                        style={{
                          width: `24px`,
                          height: `24px`,
                        }}
                      />
                      <Text
                        style={{
                          margin: `8px`,
                        }}
                        intlId="map_options.filters.unassigned"
                      />
                    </Grid>

                    <Grid item xs={2}>
                      {driversFilters[`unassigned`] === true ? (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            const newDriversFilters = { ...driversFilters };
                            newDriversFilters[`unassigned`] = false;
                            setDriversFilters(newDriversFilters);
                          }}
                        >
                          <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      ) : (
                        <EyeSelectorContainer
                          container
                          onClick={() => {
                            const newDriversFilters = { ...driversFilters };
                            newDriversFilters[`unassigned`] = true;
                            setDriversFilters(newDriversFilters);
                          }}
                        >
                          <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                        </EyeSelectorContainer>
                      )}
                    </Grid>
                  </Grid>

                  {(drivers ?? []).map((driver: Driver) => (
                    <MapFilterElement
                      key={driver.id}
                      container
                      id={`driver-filters-${driver.id}`}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      wrap="nowrap"
                    >
                      <Grid item container direction="row" alignItems="center" wrap="nowrap" xs={10}>
                        <RouteAvatar driver={driver} planDrivers={drivers} />

                        {driver.label && driver.label !== `` ? (
                          <Text
                            style={{
                              margin: `8px`,
                              whiteSpace: `nowrap`,
                              textOverflow: `ellipsis`,
                              overflow: `hidden`,
                              maxWidth: `238px`,
                            }}
                          >
                            {driver.label}
                          </Text>
                        ) : (
                          <Text
                            variant="body"
                            intlId="no_name"
                            style={{
                              margin: `8px`,
                            }}
                          />
                        )}
                      </Grid>

                      <Grid item xs={2}>
                        {driversFilters[driver.id] === true ? (
                          <EyeSelectorContainer
                            container
                            onClick={() => {
                              const newDriversFilters = { ...driversFilters };
                              newDriversFilters[driver.id] = false;
                              setDriversFilters(newDriversFilters);
                            }}
                          >
                            <VisibilityOnIcon style={{ color: RoutalPalette.primary40, cursor: `pointer` }} />
                          </EyeSelectorContainer>
                        ) : (
                          <EyeSelectorContainer
                            container
                            onClick={() => {
                              const newDriversFilters = { ...driversFilters };
                              newDriversFilters[driver.id] = true;
                              setDriversFilters(newDriversFilters);
                            }}
                          >
                            <VisibilityOffIcon style={{ color: RoutalPalette.text, cursor: `pointer` }} />
                          </EyeSelectorContainer>
                        )}
                      </Grid>
                    </MapFilterElement>
                  ))}
                </Grid>
              </Accordion>
            </div>
          </div>
        </Popover>
      ) : null}

      <div style={{ width: `4px` }} />

      <Button
        onClick={(event: any) => {
          event.stopPropagation();
          const boundingClient = event.currentTarget.getBoundingClientRect();
          setPopoverOpen({
            type: `zones`,
            top: boundingClient.top - ZONES_MENU_HEIGHT - 6,
            left: boundingClient.left,
          });
        }}
        variant="flat"
        styleType="secondary"
        style={{
          backgroundColor: RoutalPalette.white,
          margin: 0,
          width: `70px`,
          justifyContent: `flex-start`,
          borderColor: RoutalPalette.secondary00,
        }}
      >
        <img src={GeoFencesFilterIcon} alt="" draggable="false" style={{ width: `16px` }} />
        <HSpacer small />

        <div style={{ position: `relative` }}>
          <Text style={{ cursor: `pointer` }} intlId="map_options.zones" />
          {geoFencesEnabled && !allZonesAreEnabled() ? (
            <span
              style={{
                width: `6px`,
                height: `6px`,
                backgroundColor: RoutalPalette.primary40,
                borderRadius: `50%`,
                position: `absolute`,
                top: `0`,
                right: `-5px`,
              }}
            ></span>
          ) : null}
        </div>
      </Button>

      {popoverOpen?.type === `zones` ? (
        <Popover
          PaperProps={{
            variant: `outlined`,
            style: {
              border: `1px solid ${RoutalPalette.neutral10}`,
              backgroundColor: `white`,
              color: `${RoutalPalette.secondary00}`,
              boxShadow: `none`,
            },
          }}
          anchorReference="anchorPosition"
          anchorPosition={{
            top: popoverOpen.top,
            left: popoverOpen.left,
          }}
          keepMounted
          open={popoverOpen?.type === `zones`}
          onClose={(event: any) => {
            event.preventDefault();
            event.stopPropagation();
            setPopoverOpen(undefined);
          }}
        >
          <div
            style={{
              width: `${FILTERS_MENU_WIDTH}px`,
              height: `${ZONES_MENU_HEIGHT}px`,
            }}
          >
            {!geoFencesEnabled ? (
              <Grid container wrap="nowrap" direction="column" style={{ height: `100%` }}>
                <Grid
                  item
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  style={{
                    height: `54px`,
                    paddingLeft: `16px`,
                    paddingRight: `8px`,
                  }}
                >
                  <Text variant="body-large" isBold intlId="map_options.zones" style={{ cursor: `pointer` }} />
                </Grid>

                <VSpacer small />

                <Grid item container direction="column" justifyContent="center" alignItems="center">
                  <Text intlId="map_options.zones.disabled" />

                  <VSpacer small />

                  <Button
                    variant="text"
                    intlId="map_options.zones.disabled.go_to_constraints"
                    onClick={() => {
                      navigate(
                        routing.PROJECT_SETTINGS.PLANNER_FIELDS.CONSTRAINTS.replace(`:projectId`, activeProject!.id)
                      );
                    }}
                  />
                </Grid>
              </Grid>
            ) : (
              <>
                {projectGeoFences.length > 0 ? (
                  <Grid container direction="column">
                    <Grid
                      item
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      style={{
                        height: `54px`,
                        paddingLeft: `16px`,
                        paddingRight: `8px`,
                      }}
                    >
                      <Text variant="body-large" isBold intlId="map_options.zones" style={{ cursor: `pointer` }} />

                      <Switch
                        checked={allZonesAreEnabled()}
                        onChange={(event: any) => {
                          const { checked } = event.target;
                          onUpdateActiveZones({ selectedZones: checked ? projectGeoFences : [] });
                        }}
                        color="primary"
                      />
                    </Grid>

                    {projectGeoFences.map((zone) => {
                      return (
                        <Grid
                          item
                          container
                          direction="row"
                          wrap="nowrap"
                          alignItems="center"
                          style={{
                            borderTop: `1px solid #0000001f`,
                            paddingLeft: `16px`,
                            paddingRight: `8px`,
                            height: `44px`,
                          }}
                        >
                          <Grid item container direction="row" wrap="nowrap" style={{ width: `258px` }}>
                            <div
                              style={{
                                minWidth: `22px`,
                                height: `22px`,
                                borderRadius: `50%`,
                                backgroundColor: zone.color.value,
                                marginRight: `8px`,
                              }}
                            />

                            {zone.label ? (
                              <RoutalTooltip title={zone.label}>
                                <Text
                                  variant="body-large"
                                  style={{
                                    width: `230px`,
                                    textOverflow: `ellipsis`,
                                    overflow: `hidden`,
                                    whiteSpace: `nowrap`,
                                  }}
                                >
                                  {zone.label}
                                </Text>
                              </RoutalTooltip>
                            ) : (
                              <Text
                                variant="body-large"
                                intlId="no_name"
                                style={{ color: RoutalPalette.secondary00 }}
                              />
                            )}
                          </Grid>

                          <Grid item>
                            <Switch
                              checked={planGeoFences.map((pZ) => pZ.id).includes(zone.id)}
                              onChange={(event: any) => {
                                const { checked } = event.target;
                                onUpdateActiveZones({
                                  selectedZones: checked
                                    ? planGeoFences.concat(zone)
                                    : planGeoFences.filter((planZone) => planZone.id !== zone.id),
                                });
                              }}
                              value={zone.enabled && projectGeoFences.map((g) => g.id).includes(zone.id)}
                              color="primary"
                            />
                          </Grid>
                        </Grid>
                      );
                    })}

                    <Grid container justifyContent="flex-end" style={{ paddingRight: `8px` }}>
                      <Button intlId="planner.zones.create_geo_fence" onClick={() => setCreatingGeoFence(true)} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container wrap="nowrap" direction="column" style={{ height: `100%` }}>
                    <Grid
                      item
                      container
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      style={{
                        height: `54px`,
                        paddingLeft: `16px`,
                        paddingRight: `8px`,
                      }}
                    >
                      <Text variant="body-large" isBold intlId="map_options.zones" style={{ cursor: `pointer` }} />
                    </Grid>

                    <Grid item container direction="column" justifyContent="center" alignItems="center">
                      <Text intlId="map_options.zones.no_zones" />

                      <VSpacer small />

                      <Button intlId="planner.zones.create_geo_fence" onClick={() => setCreatingGeoFence(true)} />

                      <VSpacer small />

                      <Button
                        variant="text"
                        intlId="map_options.zones.go_to_zones"
                        onClick={() => {
                          navigate(routing.PLANNER.GEO_FENCES);
                        }}
                      />
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </div>
        </Popover>
      ) : null}
    </>
  );
};
